import fetch from 'cross-fetch';
import { logoutBbl } from './CommonTools';
import { appConfig } from './ConfigLoader';
import {
  redirectToMaintenancePage,
  redirectToUserOfflinePage,
} from './PageRedirection';

export type ApiHelperResponse<T = any> = {
  request: {
    url: URL;
    method: SupportedMethod;
    data?: any;
    options: ApiHelperOptions;
  };
  success: boolean;
  data?: T;
  error?: any;
};

interface ErrorResponse {
  error_code: string;
  error_msg: string;
  status_code: number;
}

export const enum SupportedMethod {
  GET = 'GET',
  POST = 'POST',
}

export const enum SupportedOutgoingFormat {
  JSON = 'application/json',
  MULTIPART = 'multipart/form-data',
}

export const enum SupportedIncomingFormat {
  JSON = 'application/json',
  TEXT = 'text/plain',
}

export const enum SupportedServers {
  BBL = 'bbl',
  ACCOUNT = 'account',
}

interface ApiHelperOptions {
  outgoingFormat?: SupportedOutgoingFormat;
  incomingFormat?: SupportedIncomingFormat;
  headerConstructor?: () => Record<string, unknown>;
  dataConstructor?: () =>
    | Record<string, unknown>
    | string
    | FormData
    | undefined;
  timeout?: number;
}

const apiHelper = async <T = any>(
  server: SupportedServers,
  endpoint: string,
  method: SupportedMethod,
  data: Record<string, unknown> | string | undefined,
  options: ApiHelperOptions,
): Promise<ApiHelperResponse<T>> => {
  // PREPARE API DATA
  const base =
    server === SupportedServers.ACCOUNT
      ? appConfig().accountServer
      : appConfig().bblServer;
  const url = new URL(endpoint, base);
  const request = { url, method, data, options };
  let headers = {};
  let dataObject;
  try {
    const constructedHeader = options.headerConstructor
      ? options.headerConstructor()
      : {};

    if (options.outgoingFormat === SupportedOutgoingFormat.JSON) {
      // JSON FORMAT
      const constructedData = options.dataConstructor
        ? options.dataConstructor()
        : {};

      dataObject = JSON.stringify({
        ...(data ? (data as Record<string, unknown>) : {}),
        ...(constructedData as Record<string, unknown>),
      });
      headers = {
        accept: options.incomingFormat,
        'Content-Type': 'application/json; charset=UTF-8',
        ...constructedHeader,
      };
    } else if (options.outgoingFormat === SupportedOutgoingFormat.MULTIPART) {
      // MULTIPART FORMAT
      const constructedData = (
        options.dataConstructor ? options.dataConstructor() : new FormData()
      ) as FormData;
      const constructedHeader = options.headerConstructor
        ? options.headerConstructor()
        : {};
      dataObject = constructedData;
      headers = {
        accept: options.incomingFormat,
        // REMOVED TO PREVENT NO BOUNDARY ERROR
        // 'Content-Type': `${SupportedOutgoingFormat.MULTIPART}; boundary=----WebKitFormBoundaryqTqJIxvkWFYqvP5s`,
        ...constructedHeader,
      };
    } else {
      headers = constructedHeader;
    }

    // TO-DO: OTHER SUPPORTED FORMAT
  } catch (err: unknown) {
    // API FAILURE DATA LEVEL
    console.error('API FAILURE DATA LEVEL', err);
    return { request: { ...request }, success: false, error: err };
  }

  // CALLING API
  let response: Response | undefined;

  // Fetch
  const retries = 1; // retry the api request 3 times
  for (let i = 0; i < retries; i++) {
    try {
      response = await fetch(url, {
        method,
        headers,
        body: dataObject,
      });
    } catch (err: unknown) {
      if (i === retries - 1) {
        // API FAILURE API LEVEL
        void handleApiLevelError(err, server);
        return { request: { ...request }, success: false, error: err };
      }

      console.log(`Request failed, retrying...`);
      await new Promise((resolve) => {
        setTimeout(() => {
          resolve(true);
        }, 1000);
      }); // wait 1 second before retrying
    }
  }

  // PROCESS API RESPONSE DATA
  let proccessedResponse: T | undefined;
  if (response !== undefined) {
    try {
      if (options.incomingFormat === SupportedIncomingFormat.JSON) {
        if (response.status > 400) {
          await handleBadRequest(response);
          return { request: { ...request }, success: false };
        }

        if (response.status === 200) {
          // INCOMMING JSON
          proccessedResponse = (await response.json()) as T;
        } else if (response.status === 202) {
          const errResponse = (await response.json()) as ErrorResponse;
          if (Number(errResponse.error_code) === 401) {
            console.error('Unauthorized API call', errResponse);
            logoutBbl();
          } else {
            throw new Error(errResponse.error_msg);
          }
        }
      }
      //
      else if (options.incomingFormat === SupportedIncomingFormat.TEXT) {
        proccessedResponse = (await response.text()) as unknown as T;
      }
    } catch (err: unknown) {
      // API FAILURE API RESPONSE
      handleApiResponseError(err, server);
      return { request: { ...request }, success: false, error: err };
    }
  }

  //
  return { request: { ...request }, success: true, data: proccessedResponse };
};

//
const handleApiLevelError = async (
  err: unknown,
  server: SupportedServers,
): Promise<void> => {
  // console.error('API FAILURE API LEVEL', typeof (err));
  console.error('API FAILURE API LEVEL', JSON.stringify(err));

  if (err instanceof Error) {
    if (err.message === 'TypeError: Network request failed') {
      if (window.navigator.onLine) {
        let errMsg = '';
        if (server === SupportedServers.BBL) {
          errMsg = 'BBL Server Offline';
        } else if (server === SupportedServers.ACCOUNT) {
          errMsg = 'Account Server Offline';
        }
        redirectToMaintenancePage(errMsg);
      } else {
        redirectToUserOfflinePage();
      }
    }
  }
};

const handleBadRequest = async (response: Response) => {
  switch (response.status) {
    case 503: {
      console.error('Server Currently Not Available.');
      break;
    }
    default: {
      try {
        const errResponse = (await response.json()) as ErrorResponse;
        console.error(errResponse);
      } catch (e: unknown) {
        console.error('Unexpected error', e);
      }
    }
  }
};

const handleApiResponseError = (
  err: unknown,
  server: SupportedServers,
): void => {
  console.error('API FAILURE API RESPONSE LEVEL', err);
};

export default apiHelper;
